import { EventEmitter } from 'events';
import { debounce } from 'underscore';
import once from 'one-event';
import { Fragment, ManifestParsedData, ErrorData, LevelDetails, LevelLoadedData, LevelSwitchingData, FragLoadedData, FragBufferedData, LoadStats, Level } from 'hls.js';

import { MediaErrorCode, NotAllowedError } from 'vbrick-player-src/MediaErrorCode';
import { userAgentUtil } from 'vbrick-player-src/UserAgentUtil';
import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';
import { IRevConnectInfo } from '@vbrick/vbrick-rev-connect/src/IRevConnectInfo';

import { BufferGapStallDetector } from './BufferGapStallDetector';
import { HlsJsStreamControllerState } from './HlsJsStreamControllerState';
import { HlsLiveResumeHelper } from './HlsLiveResumeHelper';
import { IBufferController, IConfigExtended, IEwmaBandwidthEstimator, IHlsJsExtended, ILevelExtended, IStatsExtended, ISubtitleStreamController, ITimelineController } from './IHlsJsExtended';
import { IHlsPlayerLevelsUpdatedData } from './IHlsPlayerLevelsUpdatedData';
import { IVbrickPlayerHlsConfig } from './IVbrickPlayerHlsConfig';
import { XhrWorkerLoader } from './XhrWorkerLoader';
import { getTotalTimeInSecs } from './Util';
import { hlsStatsReporterInstance } from './stats/HlsStatsReporter';
import { monkeyPatchErrorOutMethod } from './HlsErrorControllerHelper';
import { monkeyPatchFragmentLoadMethod } from './FragmentLoader';
import { xhrSetupFromConfig } from './XhrSetupFromConfig';

export const HLS_PLAYER_BUFFERING = 'buffering';
export const HLS_PLAYER_CAN_PLAY = 'canplay';
export const HLS_PLAYER_FRAG_LOADED = 'fragLoaded';
export const HLS_PLAYER_LEVEL_LOADED = 'levelLoaded';
export const HLS_PLAYER_LEVELS_UPDATED = 'levelsUpdated';
export const HLS_PLAYER_RECOVERING = 'recovering';

export const HLS_TAG_INITIAL_PROGRAM_DATE_TIME = 'vbrickstarttime';

export const LIVE_MAX_LATENCY_DUARATION_COUNT: number = 6;

//const VALIDATION_FRAG_DURATION_DIFF_THRESHOLD_SECS: number = .25;
const VALIDATION_FRAG_TARGET_DURATION_DIFF_THRESHOLD_SECS: number = .1;
const CLOSE_FRAGMENT_FIND_THRESHOLD_SECS: number = 3; // just little more than the 2secs fragment duration

let DEFAULT_XHR_LOADER;

interface IBufferStalledErrorTimeout {
	timeOfError: number;
	timeout: number;
}

interface IVerifyNudgeSuccessTimeout {
	nudgeTime: number;
	timeout: number;
}

export class HlsPlayer extends EventEmitter {
	public bwEstimate: number;
	public isRecovering: boolean;
	public player: IHlsJsExtended;
	public resumeHelper: HlsLiveResumeHelper;
	public subtitleHelperInstance;
	public HlsEvents: any;

	protected Hls;
	protected SubtitleHelper;
	protected _initialProgramDateTime: Date;
	protected bufferGapStallDetector: BufferGapStallDetector;
	protected bufferStalledErrorTimeout: IBufferStalledErrorTimeout;
	protected forceMetadataPreloading: boolean;
	protected fragErrorAutoLevelSwitchBefore: number;
	protected hlsConfig: IVbrickPlayerHlsConfig;
	protected ignoreNextDuplicateTimeUpdate: boolean;
	protected isDestroyed: boolean;
	protected isLevelLoadError: boolean;
	protected isLiveFragLoadError: boolean;
	protected isPrimaryPlayer: boolean;
	protected lastEndSN: number;
	protected lastSeekToTimeInSec: number;
	protected lastTimeUpdateInSec: number;
	protected onBufferingEventHandler: () => void;
	protected onBufferingTimeout: number;
	protected onCanPlayEventHandler: () => void;
	protected onSeekingEventHandler: () => void;
	protected onTimeUpdateEventHandler: () => void;
	protected onVideoElementCompleteEventHandler: () => void;
	protected resumeHelperClass = HlsLiveResumeHelper;
	protected samePlaylistCount: number;
	protected samePlaylistStartTime: Date;
	protected verifyNudgeSuccessTimeout: IVerifyNudgeSuccessTimeout;

	constructor(
		protected src: string,
		public videoElement: HTMLVideoElement,
		protected API: VideogularComponent,
		public isLive: boolean,
		protected hlsJsConfigOverrides: Partial<IConfigExtended>,
		protected isStartPrefetch: boolean = !isLive,
		protected revConnectInfo?: IRevConnectInfo,
	) {
		super();

		this.initPlayer();

		this.isDestroyed = false;
		this.isPrimaryPlayer = true;
	}

	public get isAutoPlay(): boolean {
		return this.API.autoPlay && !userAgentUtil.isMobile;
	}

	protected get isFragLoadWorker(): boolean {
		return this.hlsConfig.isFragLoadWorker;
	}

	protected initPlayer(): void {
		this.hlsConfig = this.API.config.hls || {};

		this.applySourceWithMSE();

		//events
		this.onBufferingEventHandler = () => this.onBuffering();
		this.onCanPlayEventHandler = () => this.onCanPlay();
		this.onSeekingEventHandler = () => this.onSeeking();
		this.onTimeUpdateEventHandler = debounce(() => this.onTimeUpdate(), 200);
		this.onVideoElementCompleteEventHandler = () => this.onVideoElementComplete();

		this.videoElement.addEventListener('waiting', this.onBufferingEventHandler);
		this.videoElement.addEventListener('stalled', this.onBufferingEventHandler);
		this.videoElement.addEventListener('canplay', this.onCanPlayEventHandler);
		this.videoElement.addEventListener('canplaythrough', this.onCanPlayEventHandler);
		this.videoElement.addEventListener('timeupdate', this.onTimeUpdateEventHandler);
		this.videoElement.addEventListener('seeking', this.onSeekingEventHandler);
		this.videoElement.addEventListener('error', this.onVideoElementError);
		this.videoElement.addEventListener('ended', this.onVideoElementCompleteEventHandler);
	}

	protected applySourceWithMSE(): Promise<void> {
		return Promise.all(
			[
				import(/* webpackChunkName: "hls.js" */ 'hls.js'), // jshint ignore:line
				import(/* webpackChunkName: "subtitleHelper" */ './helper/SubtitleHelper'), // jshint ignore:line
			])
			.then(exports => { // jshint ignore:line
				this.Hls = exports[0].default;
				this.SubtitleHelper = exports[1].SubtitleHelper;
				this.HlsEvents = this.Hls.Events;

				if (!DEFAULT_XHR_LOADER) {
					DEFAULT_XHR_LOADER = this.Hls.DefaultConfig.loader;
				}

				return this.initHlsJsPlayer();
			});
	}

	protected initHlsJsPlayer(): Promise<void> {
		const { xhrConfig } = this.hlsJsConfigOverrides;
		const { tokenConfig, authHeader } = this.API.activeSource;
		const additionalHeaders: any = { ...xhrConfig?.additionalHeaders };
		if(authHeader && new URL(this.src).origin === location.origin) {
			additionalHeaders.Authorization = authHeader;
		}

		this.hlsJsConfigOverrides = {
			...this.hlsJsConfigOverrides,
			xhrConfig: { // declarative XHR config rather than a function. Easier to override and is serializable. (Vbrick custom)
				additionalHeaders,
				mimeType: 'text/plain',
				withCredentials: !!(tokenConfig && tokenConfig.withCredentials)
			}
		};

		this.Hls.DefaultConfig.fLoader = this.isFragLoadWorker ?
			XhrWorkerLoader :
			DEFAULT_XHR_LOADER;

		this.player = new this.Hls(Object.assign({
			debug: (document as any).enableDebugHlsJs,
			abrMaxWithRealBitrate: this.hlsConfig.isBitrateObserved,
			autoStartLoad: !!this.API.autoLoad,
			enableLazyURLResolve: true,
			fragLoadPolicy: {
				default: {
					maxTimeToFirstByteMs: 10000,
					maxLoadTimeMs: 10000,
					timeoutRetry: {
						maxNumRetry: 4,
						retryDelayMs: 250,
						maxRetryDelayMs: 0
					},
					errorRetry: {
						maxNumRetry: 6,
						retryDelayMs: 250,
						maxRetryDelayMs: 8000
					}
				}
			},
			playlistLoadPolicy: {
				default: {
					maxTimeToFirstByteMs: 10000,
					maxLoadTimeMs: 20000,
					timeoutRetry: {
						maxNumRetry: 3,
						retryDelayMs: 0,
						maxRetryDelayMs: 0
					},
					errorRetry: {
						maxNumRetry: 3,
						retryDelayMs: 1500,
						maxRetryDelayMs: 8000
					}
				}
			},
			liveMaxLatencyDurationCount: LIVE_MAX_LATENCY_DUARATION_COUNT, //permitted drift from the live edge
			liveSyncDurationCount: 4,
			maxFragLookUpTolerance: 0.5,
			maxMaxBufferLength: 60, // secs max cap of how much is allowed in the buffer (default = 600secs)
			playerId: '' + performance.now(), // leveraged by the XhrWorkerLoader (Vbrick custom)
			seekHoleNudgeDuration: 0.5,
			startFragPrefech: this.isStartPrefetch,
			stretchShortVideoTrack: true,
			xhrSetup: (xhr: XMLHttpRequest) => xhrSetupFromConfig(xhr, this.hlsJsConfigOverrides.xhrConfig) // still leveraged by the playlist loader, so adapt our xhrConfig rather than require it to be duplicated
		}, this.hlsJsConfigOverrides));

		this.player.config.pLoader = this.getPlaylistLoader(this.player.config.loader) as any;

		monkeyPatchFragmentLoadMethod(this);
		if (this.isPrimaryPlayer) {
			monkeyPatchErrorOutMethod(this);
			this.subtitleHelperInstance = new this.SubtitleHelper(this);
			this.API.setSubtitleTrackInHlsPlugin = (show: boolean, subtitle: TextTrack) => this.subtitleHelperInstance.setSubtitleTrack(show, subtitle);
			this.API.disableNativeTextTrackRenderInHlsPlugin = (isDisabled: boolean) => this.subtitleHelperInstance.disableNativeTextTrackRender(isDisabled);
			if (this.isLive) {
				this.subtitleHelperInstance.assignSubtitleStreamDoTick();
			}
			this.initAudioTracksHandlers();
		}

		this.player.on(this.HlsEvents.MANIFEST_PARSED, () => this.onManifestParsed());
		this.player.on(this.HlsEvents.LEVEL_SWITCHING, (event: string, data: any) => this.onLevelsChanged(event, data));

		//autoplay
		if (this.isAutoPlay && this.player.config.autoStartLoad) {
			this.player.on(this.HlsEvents.MANIFEST_PARSED, () => this.play(true, true));
		}

		// VOD fragment loading
		if (this.forceMetadataPreloading || (!this.player.config.autoStartLoad && !this.isLive)) {
			let levelLoadResolve: () => void;
			const levelLoadPromise: Promise<void> = new Promise<void>(resolve => levelLoadResolve = resolve);

			this.player.on(this.HlsEvents.MANIFEST_PARSED, (_event, data: ManifestParsedData) => {
				const isLevelPlaylist: boolean = data.levels.length === 1
					&& !!(data.levels[0] as any).details; // Incorrect typing indicates levels as number[] instead of Level[] which is then missing details.

				if (isLevelPlaylist) { // level playlist supplied as the source
					return;
				}

				// master playlist, so need to manually load the level
				const levelController = this.levelController;
				levelController.level = this.player.nextLoadLevel = this.player.startLevel = data.levels.length - 1;

				levelController.startLoad();
			});

			this.once(HLS_PLAYER_LEVEL_LOADED, () => {
				// Get a handle on the buffering controller (no direct access)
				const bufferController: any = this.getBufferController();

				// Manually apply the duration metadata so that seek will work. Allows for us to handle seeking to the startTime.
				const applyDuration = () => {

					const isBufferedFragRequiredToSetDuration: boolean = userAgentUtil.isSafari;

					(isBufferedFragRequiredToSetDuration ?
						this.onAllowFragToBufferToSetDuration(this.startTimeSecs) :
						Promise.resolve()
					)
						.then(() => {
							// bufferController.mediaSource.duration = bufferController._levelDuration;

							// The internal Videogular implementation updates the duration on timeupdate.
							this.videoElement.dispatchEvent(this.createEvent('timeupdate'));

							// Prevent getting the player stuck in a buffering state by triggering canplay.
							this.videoElement.dispatchEvent(this.createEvent('canplay'));

							levelLoadResolve();
						});
				};

				if(bufferController.mediaSource) {
					if (bufferController.mediaSource.readyState === 'open') {
						applyDuration();
					} else {
						bufferController.mediaSource.addEventListener('sourceopen', applyDuration);
					}
				}
			});

			if (this.isAutoPlay) {
				levelLoadPromise
					.then(() => this.player.startLoad(this.startTimeSecs));
			} else { // for manual triggering, kick off loading on the first play event
				const onPlayHandler = () => {
					levelLoadPromise
						.then(() => {
							if (!this.isDestroyed) {
								this.player.startLoad(this.currentTime); // user may seek occur prior to triggering play, so use currentTime
							}
						});
				};

				if (this.isPlaying) {
					onPlayHandler();
				} else {
					if (this.videoElement) {
						once(this.videoElement, 'play')
							.then(() => onPlayHandler());
					}

				}
			}
		}

		this.player.on(this.HlsEvents.FRAG_PARSED, () => this.onFragParsed());

		this.player.on(this.HlsEvents.FRAG_BUFFERED, (_event, data) => this.onFragBuffered(data));

		this.player.on(this.HlsEvents.LEVEL_LOADED, (_event, { details, stats }: LevelLoadedData) => this.onLevelLoaded(details, stats));

		this.player.on(this.HlsEvents.LEVEL_SWITCHING, (_event, data: LevelSwitchingData) => {
			if (data.fragmentError) {
				console.info('Level is switching due to a fragment error.', data.url);
				this.fragErrorAutoLevelSwitchBefore = data.level;
			}
		});
		this.player.on(this.HlsEvents.FRAG_LOADED, (_event, data: FragLoadedData) => {
			if (this.fragErrorAutoLevelSwitchBefore) {
				console.info('Fragment has loaded following a fragment error level switch, so requesting a return to the prior level', data.frag.url);
				this.player.nextAutoLevel = this.fragErrorAutoLevelSwitchBefore;
				this.fragErrorAutoLevelSwitchBefore = null;
			}

			this.emit(HLS_PLAYER_FRAG_LOADED, data);
		});

		//wire up error handling
		this.player.on(this.HlsEvents.ERROR, (_event, data: ErrorData) => {
			const timeOfError: number = this.currentTime;
			const isBufferFullError: boolean = data.details === this.Hls.ErrorDetails.BUFFER_FULL_ERROR;
			const isFragParsingError: boolean = data.details === this.Hls.ErrorDetails.FRAG_PARSING_ERROR;

			if (this.isDestroyed || this.isLiveFragLoadError) {
				return;
			}

			if (isBufferFullError || isFragParsingError) {
				data.fatal = true;
			}

			if (data.fatal) { //handle fatal errors
				console.error(`hls.js fatal error @ ${timeOfError}:-`, data);
				this.onFatalError(timeOfError, data);
			} else { // non-fatal errors
				const ErrorDetails = this.Hls.ErrorDetails;

				console.warn(`hls.js non-fatal error @ ${timeOfError}:-`, data);

				switch (data.details) {
					case ErrorDetails.LEVEL_LOAD_ERROR:
					case ErrorDetails.LEVEL_LOAD_TIMEOUT:
						this.isLevelLoadError = true;
						break;

					case ErrorDetails.BUFFER_STALLED_ERROR:
					case ErrorDetails.BUFFER_SEEK_OVER_HOLE:

						//AV-30675 - Adding a workaround for safari. with latest hls.js, some video in safari triggering buffer stalled at the end of video.
						const isSafariPlaybackAtEnd = userAgentUtil.isSafari && this.currentTimeIsAtVodEndTimeEdge();
						if (isSafariPlaybackAtEnd) {
							console.warn(`safari current time -- ${this.currentTime} is near video ending ${this.videoElement.duration}`);
							this.seekTo(this.videoElement.duration, true);
							//since playback is almost eneded.. just Triggering ended event to avoid additional buffering spinner.
							this.videoElement.dispatchEvent(this.createEvent('ended'));
							return;
						}

						const isAtBufferEdge = this.isAtBufferEdge();
						const recoverBufferStalledError = !isAtBufferEdge || isAtBufferEdge && this.streamController.state === HlsJsStreamControllerState.IDLE;

						// Chrome does not seem to like being nudged while simply waiting at the edge of the buffer for more data and will resume when undisturbed. Not so on others.
						if (data.fatal || recoverBufferStalledError) {
							this.onBufferStalledError(timeOfError);
						} else if(isAtBufferEdge) {
							this.atBufferEdgeStateWorkaround();
						}
						break;

					case ErrorDetails.INTERNAL_EXCEPTION:
						console.warn('hls.js internal exection detail', (data as any).error);
						break;
				}
			}
		});
		this.player.on(this.HlsEvents.SUBTITLE_TRACK_LOADED, (_event, data) => this.subtitleHelperInstance.onSubtitleTrackLoaded(data));

		this.player.loadSource(this.src);
		this.attachToVideoElement();

		this.resumeHelper = new this.resumeHelperClass(this, this.API);
		this.resumeHelper.initialize();

		this.bufferGapStallDetector = new BufferGapStallDetector(this);
		return Promise.resolve();
	}

	/**
	 * Workaround for browser's (like - IE11, macOS safari) limitation where, despite having applied a duration through the Media Source,
	 * it does not permit seeking until some data is populated in the buffer (readyState > 0).
	 * The hls.js implementation takes an all or nothing approach to prefetching segements, so this gets a bit hacky.
	 * 1) Start loading at the givent startTimeSecs
	 * 2) On loading of the first fragment, throw the hls.js StreamController into the STOPPED state to prevent further loading.
	 * 3) When the first fragment is buffered, start loading of the player and resolve returned promise.
	 */
	protected onAllowFragToBufferToSetDuration(startTimeSecs: number): Promise<void> {
		const streamController = this.streamController;

		return new Promise(resolve => {
			const onFirstFragBuffered = () => {
				this.removeListener(HLS_PLAYER_RECOVERING, onPlayerRecovering);

				window.setTimeout(() => {
					this.player.stopLoad();
					resolve();
				}, 0); // timeout gets it playing nice
			};
			const onPlayerRecovering = () => this.player.off(this.HlsEvents.FRAG_BUFFERED, onFirstFragBuffered);

			this.player.once(this.HlsEvents.FRAG_LOADING, () => {
				streamController.state = HlsJsStreamControllerState.STOPPED;
			});
			this.player.once(this.HlsEvents.FRAG_BUFFERED, onFirstFragBuffered);

			this.once(HLS_PLAYER_RECOVERING, onPlayerRecovering);

			this.player.startLoad(startTimeSecs);
		});
	}

	/**
	 * Applies the internally supplied real bitrate to the bitrate value of the given buffered level.
	 * Then proportionally updates the bitrates of the other levels to bring them reasonably in line to facilitate the ABR level selection.
	 */
	protected applyRealBitrateToLevels(bufferedLevel: ILevelExtended): void {
		//had to use Object.assign because bitrate is read-only on Hls v1.
		this.player.levels.forEach(level => {
			Object.assign(level, {
				bitrate:  Math.floor(
					bufferedLevel === level ?
						level.realBitrate :
						level.playlistBitrate * (bufferedLevel.realBitrate / bufferedLevel.playlistBitrate)
				)
			});
		});
	}

	/**
	 * When the buffer stalls, hls.js seems to get stuck in a fragment loading state, despite no activity taking place. This resets it back to an idle state.
	 */
	protected atBufferEdgeStateWorkaround(): void {
		const streamController = this.streamController;

		if (streamController.state === HlsJsStreamControllerState.FRAG_LOADING) {
			streamController.state = HlsJsStreamControllerState.IDLE;
		}
	}

	public onLiveResume(): void {
		if (this.isPrimaryPlayer && this.subtitleHelperInstance) {
			this.subtitleHelperInstance.onCleanupSubtitlesData();
		}
	}

	protected attachToVideoElement(): void {
		this.player.attachMedia(this.videoElement);
	}

	protected checkAgainstLastSeekTime(currentTime: number): boolean {
		return this.toFixed(currentTime) === this.toFixed(this.lastSeekToTimeInSec);
	}

	/**
	 * sometimes when live video is paused and restart
	 * it takes very long time to load video. hls.js buffer not remain in the range
	 * video frags time. So, seeking to liveSyncPosition improves the playback after pause and start.
	 */
	protected recoverFromInitialBufferingStuck(): void {
		const position = this.streamController.liveSyncPosition;
		if (this.currentTime === 0 && position > 0) {
			console.warn('current time still at 0 but liveSyncPosition has moved. so seekin', position);
			this.seekTo(position, true);
		}
	}

	protected checkForLoadingStuck(): void {
		const currentTime: number = this.currentTime;
		const streamController = this.streamController;
		const fragCurrent = streamController.fragCurrent;
		const liveSyncPosition: number = streamController.liveSyncPosition;

		// stream controller set to the FRAG_LOADING state, but the current frag doesn't even have a loader
		if (fragCurrent && !fragCurrent.loader && streamController.state === HlsJsStreamControllerState.FRAG_LOADING) {
			console.warn(`${fragCurrent._url} - Stream controller is stuck, so adjusting its state back to idle`);
			streamController.state = HlsJsStreamControllerState.IDLE; // should get it running again on the next tick
		}

		if (
			fragCurrent &&
			currentTime &&
			currentTime < fragCurrent.start &&
			!this.isBufferedAt(currentTime) &&
			liveSyncPosition &&
			isFinite(liveSyncPosition)
		) {
			console.warn(`${fragCurrent._url} - Playback is stuck in unbuffered region, so seeking (currentTime=${currentTime} with fragCurrent.start=${fragCurrent.start})`);

			const deltaPTS = (fragCurrent as any).deltaPTS || 0;
			const seekToPosition: number = this.isBufferedAt(liveSyncPosition) ?
				liveSyncPosition :
				fragCurrent.start + deltaPTS + 0.01;

			this.videoElement.currentTime = seekToPosition;
		}
	}

	public get streamController() {
		return this.getPlayerInternalProperty('streamController');
	}

	protected getBufferController(): IBufferController {
		return this.getPlayerInternalProperty('coreComponents').find(component => 'mediaSource' in component);
	}

	public getTimelineController(): ITimelineController {
		return this.getPlayerInternalProperty('coreComponents').find(component => 'initPTS' in component);
	}

	public get levelController() {
		return this.getPlayerInternalProperty('levelController');
	}

	public get abrController() {
		return this.getPlayerInternalProperty('abrController');
	}


	public get hlsUrl(): string {
		return this.getPlayerInternalProperty('url');
	}


	private getPlayerInternalProperty(propName: string): any {
		/* eslint-disable */
		return this.player[propName];
	}

	protected getInitialProgramDateTimeFromTag(firstFragment: Fragment): Date {
		// have to workaround faulty hls.js parsing since our value containing a colon throws it off
		const initialProgramDateTimeTag = firstFragment.tagList
			.find(([tagKey]) => tagKey.startsWith(HLS_TAG_INITIAL_PROGRAM_DATE_TIME));

		if (!initialProgramDateTimeTag) {
			return null;
		}

		// get the value from a correct split of the tag entry and cast to a Date
		const rejoinedTag = initialProgramDateTimeTag.join(':');
		const [, intialProgramDateTimeValue] = rejoinedTag.split(HLS_TAG_INITIAL_PROGRAM_DATE_TIME + ':');

		return new Date(intialProgramDateTimeValue.trim());
	}

	protected getPlaylistLoader(defaultLoader: any) {
		const { timeoutRetry, errorRetry } = this.player.config.manifestLoadPolicy.default;
		const isManifestLoadingMaxRetryConfigured: boolean = timeoutRetry.maxNumRetry > 1 || errorRetry.maxNumRetry > 1;
		const isLive = this.isLive;
		const disableLiveKeepAlive = this.hlsConfig.disableLiveKeepAlive;

		const reshapeXMLHttpRequest = (oldXhr: XMLHttpRequest, status: number) => {
			const xhr = {
				onabort: oldXhr.onabort,
				onerror: oldXhr.onerror,
				onload: oldXhr.onload,
				onloadend: oldXhr.onloadend,
				onloadstart: oldXhr.onloadstart,
				onprogress: oldXhr.onprogress,
				ontimeout: oldXhr.ontimeout,
				onreadystatechange: oldXhr.onreadystatechange,
				readyState: oldXhr.readyState,
				response: oldXhr.response,
				responseText: oldXhr.responseText,
				responseType: oldXhr.responseType,
				responseURL: oldXhr.responseURL,
				status,
				statusText: oldXhr.statusText,
				timeout: oldXhr.timeout,
			};
			return xhr;
		}

		return class HlsPlayerPlaylistLoader extends defaultLoader {
			readystatechange(event) {
				const xhr = event.currentTarget;
				const isEndTagAvailable = xhr.readyState === 4 && xhr.responseText?.includes('#EXT-X-ENDLIST');

				//workaround - if live playback encounters end tag (it happens when live enrichment playback stopped)then
				// force it to retry mechanism. retry count is same as levelLoading retry count.
				if (!disableLiveKeepAlive && isLive && isEndTagAvailable) {
					const newXhr = reshapeXMLHttpRequest(this.loader, 0); //this will trigger a non-fatal level load error.
					newXhr.responseText = '';
					newXhr.response = '';
					newXhr.statusText = '';
					this.loader = newXhr;
					super.readystatechange();
					return;
				}

				//workaround XhrLoader not retrying the playlist fetch with status in 400s or
				//xhr status code getting changed to 0 because of CORS. supplying different code to force retry.
				if (
					isManifestLoadingMaxRetryConfigured &&
					xhr.readyState === 4 &&
					xhr.status === 404 || xhr.status < 1
				) {
					console.error('player receiving status code', xhr.status);
					const newXhr = reshapeXMLHttpRequest(this.loader, 300);
					this.loader = newXhr;
					super.readystatechange();
				} else {
					super.readystatechange();
				}
			}
		}
	}

	protected isAtBufferEdge(): boolean {
		const bufferedLength: number = this.videoElement.buffered.length;
		const bufferEnd: number = bufferedLength ?
			this.toFixed(this.videoElement.buffered.end(bufferedLength - 1), 1) :
			0;
		const currentTime: number = this.toFixed(this.currentTime, 1);

		return currentTime &&
			this.isBuffering &&
			currentTime >= bufferEnd;
	}

	protected isBufferedAt(timeSec: number): boolean {
		return Array.from(this.videoElement.buffered as any)
			.reduce<boolean>(
			(output, _currentValue, currentIndex) => output ||
					(
						timeSec >= this.videoElement.buffered.start(currentIndex) &&
						timeSec <= this.videoElement.buffered.end(currentIndex)
					),
			false
		);
	}

	protected onFatalError(timeOfError: number, data: ErrorData): void {
		const isMediaError: boolean = data.type === this.Hls.ErrorTypes.MEDIA_ERROR;
		const isLiveFragLoadError: boolean = this.isLive && (data.details === this.Hls.ErrorDetails.FRAG_LOAD_ERROR || data.details === this.Hls.ErrorDetails.FRAG_PARSING_ERROR);
		const isFragLoopLoadingError: boolean = data.details === this.Hls.ErrorDetails.FRAG_LOOP_LOADING_ERROR;
		const isBufferFullError: boolean = data.details === this.Hls.ErrorDetails.BUFFER_FULL_ERROR;
		const isSamePlaylistStarvation: boolean = data.details === this.Hls.ErrorDetails.BUFFER_STALLED_ERROR && !!this.samePlaylistCount;
		const isRecoverableError: boolean = isMediaError || isFragLoopLoadingError || isBufferFullError;

		//atempt to recover from media errors
		if (isLiveFragLoadError) {
			this.onLiveFragLoadError();
		} else if (timeOfError >= 1 && isRecoverableError && !isSamePlaylistStarvation) { //ignore false positives at t=0
			console.info(`hls.js recoverable error fired @  ${timeOfError}`);

			this.recoverAfterTimeout(timeOfError);
		} else {
			const errorEvent: Event & { details?: any } = this.createEvent('hlsNonRecoverableError');
			errorEvent.details = data.details;

			this.API.onVideoError(errorEvent);
		}
	}

	protected onBufferStalledError(timeOfError: number): void {
		if (this.isLevelLoadError) {
			return;
		}

		//errors may come in out of sequence, so ignore those prior to the existing timeout
		if (this.bufferStalledErrorTimeout && timeOfError <= this.bufferStalledErrorTimeout.timeOfError) {
			return;
		}

		//cancel an existing timeout initiated at a prior time
		if (this.bufferStalledErrorTimeout) {
			window.clearTimeout(this.bufferStalledErrorTimeout.timeout);
		}

		//create a new timeout
		this.bufferStalledErrorTimeout = {
			timeOfError,
			timeout: window.setTimeout(() => {
				if (this.currentTime === timeOfError) { //no change in player time since the error, so give it a nudge
					console.warn(`onBufferStalledError timeout @ ${timeOfError}, so nudging`);

					let nudgeTime: number;

					if (this.currentTime + 1 <= this.videoElement.duration) {
						nudgeTime = this.currentTime + 1; // nudge forward a second
					} else if (!this.isLevelLoadError && !this.samePlaylistCount) { // as long as we're not stuck with the playlist not getting updated
						nudgeTime = this.currentTime - 0.5; // near the end, so try nudging back a little to revive it
					} else {
						return;
					}

					this.seekTo(nudgeTime);
					this.verifyNudgeSuccess(nudgeTime);
				}

				this.bufferStalledErrorTimeout = null;
			}, 1000)
		};
	}

	protected onFragBuffered(data: FragBufferedData): void {
		const frag: Fragment = data.frag;
		const stats: LoadStats = data.stats;

		this.bwEstimate = stats.bwEstimate;

		this.validateFragDuration(frag);

		this.checkForBufferingMismatch();

		const bufferedLevel: ILevelExtended = this.player.levels[frag.level];

		if (this.hlsConfig.isBitrateObserved) {
			this.applyRealBitrateToLevels(bufferedLevel);
		}

		hlsStatsReporterInstance.reportFragBuffered(frag, stats, this.getBandwidthEstimate(), bufferedLevel, this.isPrimaryPlayer);
	}

	protected onFragParsed(): void {
		if (this.hlsConfig.isParseTimeIgnored) {
			const { stats } = this.streamController.fragCurrent as Fragment;

			stats.parsing = stats.loading; // effectively convey a parse time of 0 so that it won't be factored into bw estimation
		}
	}

	protected onLevelLoaded(details: LevelDetails, stats: LoadStats): void {
		if (this.revConnectInfo && this.revConnectStatsManagerInstance) {
			const playlistUrl: string = 'url' in (details as any) ? (details as any).url : null;
			this.revConnectStatsManagerInstance.recordStatsPlaylist(playlistUrl, (stats as any).loaded);
		}

		this.isLevelLoadError = false;

		const [firstFragment] = details.fragments;
		this._initialProgramDateTime = this.getInitialProgramDateTimeFromTag(firstFragment) || new Date(firstFragment.programDateTime);

		this.checkForPlaylistChange(details, stats);

		if (this.isLive) {
			this.recoverFromInitialBufferingStuck();
			this.checkForLoadingStuck();
		}

		this.emit(HLS_PLAYER_LEVEL_LOADED, { details });
	}

	protected onLiveFragLoadError(): void {
		this.isLiveFragLoadError = true;

		const currentLevel = this.getCurrentLevel();
		const targetDurationSec: number = currentLevel.details.targetduration;

		//AV-30715 - Recovering immediate in case of safari issue. with hls.js upgrade to 1.x.x - can't wait too much for safari. It gets into buffering and never recovers
		const recoverAfterMs: number = userAgentUtil.isSafari ? 1000 * 2 : targetDurationSec * 1000 * 2; // found this to be about right

		console.info('Recovering after an extended wait of ', recoverAfterMs / 1000, `. Current level target duration = ${targetDurationSec} sec`);

		window.setTimeout(() => this.recoverLiveFragLoadError(), recoverAfterMs);
	}

	protected onLevelsChanged(event?: string, data?: any): void {
		this.emit(HLS_PLAYER_LEVELS_UPDATED, this.getLevelsModel());
		//if stream switch happens manually or automactically, HlsJs gets reloaded.
		//We need to set subtitle to HlsJs.
		this.setActiveSubtitle();
	}

	protected onManifestParsed(): void {
		// copy the supplied playlist level bitrates into another property so that they aren't lost after overriding `bitrate` with the observed value
		this.player.levels.forEach(level => (level.playlistBitrate = level.bitrate));

		this.onLevelsChanged();
	}

	/**
	 * setting active subtitle when stream switch happens.
	 */
	protected setActiveSubtitle(): void {
		if (this.isPrimaryPlayer && this.API) {
			const { isHtmlSubtitleRenderingEnabled, activeSubtitle } = this.API;
			if (activeSubtitle) {
				this.subtitleHelperInstance.disableNativeTextTrackRender(isHtmlSubtitleRenderingEnabled);
				this.subtitleHelperInstance.setSubtitleTrack(!isHtmlSubtitleRenderingEnabled, activeSubtitle);
			}
		}
	}

	protected onSeeking(): void {
		this.ignoreNextDuplicateTimeUpdate = true;
	}

	protected onVideoElementComplete(): void {
		//AV-26970 - video element started triggering complete event for live video play through VBM (multicast)
		// Reason is unknown. So, recovering it from it by seek ahead a bit and triggering play.
		if (userAgentUtil.isSafari
				&& this.isPrimaryPlayer && this.isLive
				&& this.videoElement.ended && !this.videoElement.error) {
			this.seekTo(this.videoElement.currentTime + 0.02);
			this.play();
		}
	}

	protected recoverLiveFragLoadError(): void {
		if (this.isDestroyed) {
			return;
		}

		this.recover();
		this.isLiveFragLoadError = false;
	}

	protected checkFragmentTracker(): void {
		const streamController = this.streamController as any;
		const { fragCurrent, fragmentTracker, state } = streamController;
		if (!fragCurrent) {
			console.warn('no fragCurrent while executing checkFragmentTracker');
			return;
		}

		//getFragmentKey is no longer available on fragmentTracker. it's an internal function now. copied the body.
		const fragKey: string = `${fragCurrent.type}_${fragCurrent.level}_${fragCurrent.urlId}_${fragCurrent.sn}`;

		if (state === 'IDLE' && fragmentTracker?.getState(fragCurrent || {}) === 'APPENDING') {
			console.warn('Caught streamController in IDLE state with the tracker for fragCurrent in APPENDING, so clearing its tracker');
			delete fragmentTracker.fragments[fragKey];
		}
	}

	protected verifyNudgeSuccess(nudgeTime: number): void {
		const verifyNudgeSuccessOnTimeUpdate = () => {
			if (!this.isDestroyed && this.verifyNudgeSuccessTimeout) {
				console.info(`verifyNudgeSuccess - cancelling timeout since time updated to ${this.videoElement.currentTime}`);
				window.clearTimeout(this.verifyNudgeSuccessTimeout.timeout);
				this.verifyNudgeSuccessTimeout = null;
			}
		};

		if (this.verifyNudgeSuccessTimeout) {
			window.clearTimeout(this.verifyNudgeSuccessTimeout.timeout);
		}

		this.verifyNudgeSuccessTimeout = {
			nudgeTime,
			timeout: window.setTimeout(() => {
				this.videoElement.removeEventListener('timeupdate', verifyNudgeSuccessOnTimeUpdate);

				if (this.currentTime === nudgeTime && this.videoElement.seeking) {
					console.error(`hls.js nudge failure @ ${nudgeTime}, so recovering.`);
					this.recover();
				}

				this.verifyNudgeSuccessTimeout = null;
			}, 5000)
		};

		once(this.videoElement, 'timeupdate')
			.then(() =>  verifyNudgeSuccessOnTimeUpdate());
	}

	protected recoverAfterTimeout(timeOfError: number): void {
		window.setTimeout(() => {
			if (this.currentTime && this.currentTime === timeOfError) {
				console.info(`hls.js recovery - time remains @ ${timeOfError}, so trying to recover...`);
				this.recover();
			} else {
				console.info('hls.js recovery - time differs, so ignoring the false-positive.');
			}
		}, 100);
	}

	protected checkForPlaylistChange(newLevelDetails: LevelDetails, stats: LoadStats): void {
		const loggingThreshold: number = 3;
		const newEndSN: number = newLevelDetails.endSN;

		if (this.isLive) {
			if (newEndSN === this.lastEndSN) { //no change in the end segment number
				const currentDate = new Date();

				if (!this.samePlaylistCount) { //if the first, capture the start time
					this.samePlaylistStartTime = currentDate;
				}

				++this.samePlaylistCount;

				if (this.samePlaylistCount >= loggingThreshold) {
					console.warn(`${currentDate} - Same playlist received. count = ${this.samePlaylistCount}, endSN = ${newEndSN}, tRequest = ${stats.loading.start}, duration = ${performance.now() - stats.loading.start}ms, src = ${this.src}`);

					// while starved, it doesn't trigger the buffering state
					if (this.isBuffering) {
						this.videoElement.dispatchEvent(this.createEvent('waiting'));
					}
				}
			} else {
				if (this.samePlaylistCount >= loggingThreshold) {
					const samePlaylistStartTime = this.samePlaylistStartTime?.getTime()
					const durationSecs: number = samePlaylistStartTime ? (new Date().getTime() - samePlaylistStartTime) / 1000 : 0;

					console.warn(`New playlist received after receiving the same over ${this.samePlaylistCount} requests over ${durationSecs} secs. ${newEndSN - this.lastEndSN} additional segments.`);
				}

				this.samePlaylistCount = 0;
				this.samePlaylistStartTime = null;

				this.checkForBufferingMismatch();
			}

			this.lastEndSN = newEndSN;
		}
	}

	protected checkForBufferingMismatch(): void {
		if (!this.isBuffering && this.API.isBuffering && this.isPlaying) {
			console.warn('Correcting mismatch. Video element is not buffering, but player API is in buffering state');

			this.videoElement.dispatchEvent(this.createEvent('playing'));
		}
	}

	protected getCurrentLevel(): Level {
		const currentLevelIndex: number = this.getCurrentLevelIndex();

		if (!this.player.levels || !this.player.levels.length || (typeof currentLevelIndex !== 'number')) {
			return null;
		}

		return this.player.levels[currentLevelIndex];
	}

	protected getCurrentLevelIndex(): number {
		return this.player.loadLevel || 0;
	}

	protected onBuffering(): void {
		if (this.onBufferingTimeout) {
			return;
		}

		this.onBufferingTimeout = window.setTimeout(() => {
			this.onBufferingTimeout = null;

			if (this.isBuffering) {
				this.emit(HLS_PLAYER_BUFFERING);
			}
		}, 100);
	}

	protected onCanPlay(): void {
		if (this.onBufferingTimeout) {
			window.clearTimeout(this.onBufferingTimeout);
		}

		this.emit(HLS_PLAYER_CAN_PLAY);
	}

	protected onTimeUpdate(): void {
		const timeInSec = this.currentTime;

		if (this.checkAgainstLastSeekTime(timeInSec)) {
			this.ignoreNextDuplicateTimeUpdate = false;
			return;
		}

		if (this.isPlaying && this.toFixed(timeInSec) === this.toFixed(this.lastTimeUpdateInSec) && !this.isBuffering) {
			if (this.ignoreNextDuplicateTimeUpdate) {
				this.ignoreNextDuplicateTimeUpdate = false;
				return;
			}

			// possibly stuck, so give it a nudge
			const nudgeTime: number = this.currentTime + 0.5;
			this.seekTo(nudgeTime);
			this.verifyNudgeSuccess(nudgeTime);
		}

		this.lastTimeUpdateInSec = timeInSec;
	}

	protected onVideoElementError = () => {
		const videoError: MediaError = this.videoElement.error || {} as MediaError;

		// Attempt to recover from decoding errors during live playback.
		// recover() seems to be too aggressive, throwing it off further.
		if (this.isLive && videoError.code === MediaErrorCode.DECODE) {
			this.player.recoverMediaError();
			this.play();
			this.player.startLoad();
		}
	};

	protected toFixed(value: number, precision: number = 2): number {
		return typeof value === 'number' ?
			+value.toFixed(precision) :
			null;
	}

	public getBandwidthEstimate(): number {
		const bwEstimator: IEwmaBandwidthEstimator = this.abrController._bwEstimator;

		return bwEstimator ?
			bwEstimator.getEstimate() :
			-1;
	}

	public play(broadcastEvent: boolean = false, playOnCatch: boolean = false): void {
		const isAnalyticsEnabled: boolean = this.API.analyticsEnabled;

		if (!broadcastEvent && isAnalyticsEnabled) {
			this.API.analyticsEnabled = false;
		}

		try {
			const playPromise = this.videoElement.play();

			if (playPromise && playPromise.catch) {
				playPromise.catch(err => {
					if (err.name === NotAllowedError) {
						console.warn('HlsPlayer play method', err, err.name);
						this.API?.resetVolumeIfNeeded?.();
						this.play();
					}
					if (playOnCatch) {
						this.play();
					}
				});
			}
		} catch (ex) {
			//eat the error since it's not of any concern
		}

		if (!broadcastEvent && isAnalyticsEnabled) {
			this.API.analyticsEnabled = true;
		}
	}

	public pause(broadcastEvent: boolean = false): void {
		const isAnalyticsEnabled: boolean = this.API.analyticsEnabled;

		if (!broadcastEvent && isAnalyticsEnabled) {
			this.API.analyticsEnabled = false;
		}

		this.API.pause();

		if (!broadcastEvent && isAnalyticsEnabled) {
			this.API.analyticsEnabled = true;
		}
	}

	public seekTo(timeInSecs: number, forceSeek?: boolean): void {
		if (!forceSeek) {
			if (
				!this.videoElement ||
				typeof timeInSecs !== 'number' ||
				timeInSecs < 0 ||
				isNaN(this.videoElement.duration) ||
				this.videoElement.readyState === 1 ||
				timeInSecs > this.videoElement.duration
			) {
				this.checkFragmentTracker();
				return;
			} else if (this.videoElement.error) {
				this.recover();
				return;
			}
		}
		this.videoElement.currentTime = this.lastSeekToTimeInSec = timeInSecs;
	}

	public get isPlaying(): boolean {
		return this.videoElement && !this.videoElement.paused;
	}

	public get isBuffering(): boolean {
		return this.videoElement && this.videoElement.readyState < 3;
	}

	public get bitrate(): number | null {
		const level: Level = this.getCurrentLevel();

		return level ?
			level.bitrate :
			null; //player.currentLevel may be -1 (auto), so have to dive deeper to get the present level
	}

	public get currentTime(): number {
		return this.videoElement ? this.videoElement.currentTime : 0;
	}

	public recover(): void {
		const isPlaying: boolean = this.isPlaying;

		this.isRecovering = true;

		this.emit(HLS_PLAYER_RECOVERING);

		if (isPlaying) {
			this.pause();
		}

		this.player.swapAudioCodec();
		this.player.recoverMediaError();

		if (this.isLive) { // reset so that it will start fresh rather than from same position
			this.player.stopLoad();
			this.streamController.lastCurrentTime = -1;
		}

		this.player.startLoad();

		if (isPlaying && !this.isPlaying) {
			this.play();
		}

		this.isRecovering = false;
	}

	public getCurrentProgramDateTime(): Date {
		const lastDateTimeFrag: Fragment = this.getProgramDateTimeFragmentForPosition(this.currentTime);

		if (!lastDateTimeFrag) {
			return;
		}

		const timeDifferenceInMs: number = (this.currentTime - lastDateTimeFrag.start) * 1000;

		return new Date(lastDateTimeFrag.programDateTime + timeDifferenceInMs);
	}

	public getProgramDateTimeFragmentForPosition(positionInSecs: number): Fragment {
		return this.findLastFragment(fragment => fragment.programDateTime && fragment.start <= positionInSecs, false, positionInSecs, 'getProgramDateTimeFragmentForPosition');
	}

	public findClosestProgramDateTimeFragment(dateTime: Date): Fragment {
		//AV-31450 - content player not able to find the closest fragment for the given date time and sometimes
		// difference is less than 3 secs.
		return this.findLastFragment(fragment => {
				const inputTimeInSecs = getTotalTimeInSecs(dateTime)
				const fragmentTimeInSecs = fragment.programDateTime ? getTotalTimeInSecs(new Date(fragment.programDateTime)) : 0;

				return fragmentTimeInSecs <= inputTimeInSecs || ((fragmentTimeInSecs - inputTimeInSecs) < CLOSE_FRAGMENT_FIND_THRESHOLD_SECS);
			},
			true,
			dateTime,
			'findClosestProgramDateTimeFragment');
	}

	public findLastFragment(testFunction: (fragment: Fragment) => boolean, enableLogging?: boolean, currentPos?: any, methodName?: string): Fragment {
		if (!this.player) {
			return;
		}

		const currentLevel: Level = this.getCurrentLevel();

		if (!currentLevel) {
			return;
		}

		const frags: Fragment[] = this.getFragmentsForLevel(currentLevel);

		for (let fragIndex = frags.length - 1; fragIndex >= 0; --fragIndex) {
			const fragment: Fragment = frags[fragIndex];

			if (testFunction(fragment)) {
				return fragment;
			}
		}

		if (enableLogging && frags?.length) {
			const firstFrag = frags[0];
			const lastFrag = frags[frags.length - 1];
			console.warn('no fragment found. current pos, first frag sn-start-programDate, last frag sn-start-programDate', methodName, currentPos, `${firstFrag.sn}--${firstFrag.start}--${firstFrag.programDateTime}`, `${lastFrag.sn}--${lastFrag.start}--${lastFrag.programDateTime}`);
		}

	}

	public get initialProgramDateTime(): Date {
		return this._initialProgramDateTime;
	}

	public get levels(): Level[] {
		return this.player && this.player.levels ? this.player.levels : [];
	}

	public getFragmentsForLevel(level: Level): Fragment[] {
		return level.details && level.details.fragments ?
			level.details.fragments :
			[];
	}

	public getLevelsModel(): IHlsPlayerLevelsUpdatedData {
		if (!this.player) {
			return null;
		}

		return {
			activeLevelIndex: this.getCurrentLevelIndex(),
			levels: this.levels,
			selectedLevelIndex: this.player.manualLevel,
			setLevel: (level: number) => this.player.nextLevel = level
		};
	}

	public seekToLiveSyncPosition(): void {
		const liveSyncPosition: number = this.streamController.liveSyncPosition;

		if (isFinite(liveSyncPosition)) {
			this.seekTo(liveSyncPosition);
		}
	}

	public destroy(): void {
		this.isDestroyed = true;

		this.videoElement.removeEventListener('waiting', this.onBufferingEventHandler);
		this.videoElement.removeEventListener('stalled', this.onBufferingEventHandler);
		this.videoElement.removeEventListener('canplay', this.onCanPlayEventHandler);
		this.videoElement.removeEventListener('canplaythrough', this.onCanPlayEventHandler);
		this.videoElement.removeEventListener('timeupdate', this.onTimeUpdateEventHandler);
		this.videoElement.removeEventListener('seeking', this.onSeekingEventHandler);
		this.videoElement.removeEventListener('error', this.onVideoElementError);
		this.videoElement.removeEventListener('ended', this.onVideoElementCompleteEventHandler);

		if (this.resumeHelper) {
			this.resumeHelper.destroy();
			this.resumeHelper = null;
		}

		if (this.bufferGapStallDetector) {
			this.bufferGapStallDetector.destroy();
			this.bufferGapStallDetector = null;
		}

		try {
			this.videoElement.pause();
		} catch(ex) {
			// deliberately empty
		}

		if (this.bufferStalledErrorTimeout) {
			window.clearTimeout(this.bufferStalledErrorTimeout.timeout);
		}

		if (this.verifyNudgeSuccessTimeout) {
			window.clearTimeout(this.verifyNudgeSuccessTimeout.timeout);
		}

		if (this.player) {
			try {
				XhrWorkerLoader.onPlayerDestroy(this.player.config.playerId);
				this.player.destroy();
			} catch(ex) {
				// deliberately empty
			}
		}

		this.player = this.videoElement = undefined;
	}

	protected createEvent(eventType: string): Event {
		const event = document.createEvent('Event');

		event.initEvent(eventType, true, true);

		return event;
	}

	private get startTimeSecs(): number {
		return this.API?.startTimeFetchFunc?.() || 0;
	}

	private validateFragDuration(frag: Fragment): void {
		if (frag.type === 'audio') {
			return;
		}

		const targetDurationSecs: number = this.toFixed(this.levels[frag.level].details.targetduration);
		const fragActualDurationSecs: number = this.toFixed(frag.duration);
		const fragPlaylistDurationSecs = this.toFixed(+frag.tagList
			.find(([key]) => key === 'INF')[1]);
		/*const fragDurationDifference: number = this.toFixed(fragActualDurationSecs - fragPlaylistDurationSecs);
		const startPTS: number = this.toFixed(frag.startPTS);
		const endPTS: number = this.toFixed(frag.endPTS);
		const calcDuration: number = this.toFixed(endPTS - startPTS);

		if (fragDurationDifference > VALIDATION_FRAG_DURATION_DIFF_THRESHOLD_SECS) {
			console.warn(`[VALIDATION] Frag duration difference exceeds threshold of ${VALIDATION_FRAG_DURATION_DIFF_THRESHOLD_SECS} secs. Playlist = ${fragPlaylistDurationSecs}, Actual = ${fragActualDurationSecs}, Difference = ${fragDurationDifference} secs, startPTS=${startPTS}, endPTS=${endPTS}, calcDuration=${calcDuration}, Frag = ${frag.url}`);
		}*/

		if (
			fragPlaylistDurationSecs - targetDurationSecs >= VALIDATION_FRAG_TARGET_DURATION_DIFF_THRESHOLD_SECS ||
			fragActualDurationSecs - targetDurationSecs >= VALIDATION_FRAG_TARGET_DURATION_DIFF_THRESHOLD_SECS
		) {
			console.warn(`[VALIDATION] Frag duration exceeds the playlist target duration of ${targetDurationSecs} secs. Playlist = ${fragPlaylistDurationSecs}, Actual = ${fragActualDurationSecs}, Target = ${targetDurationSecs} secs, Frag = ${frag.url}`);
		}
	}

	private get revConnectStatsManagerInstance() {
		return (window as any).vbRevConnect?.RevConnectStatsManagerInstance;
	}

	private currentTimeIsAtVodEndTimeEdge(): boolean {
		return !this.isLive && (this.toFixed(this.videoElement.duration, 1) - this.toFixed(this.videoElement.currentTime, 1) <= 0.2);
	}

	private initAudioTracksHandlers(): void {
		if (this.isPrimaryPlayer) {
			this.API.setAudioTrackInHlsPlugin = (audioTrackId: number) => {
				if (this.player.audioTrack !== audioTrackId) {
					this.player.audioTrack = audioTrackId;
				}
			}
			this.player.on(this.HlsEvents.AUDIO_TRACK_SWITCHED, (event, data) => {
				this.API.setActiveAudioTrackId(data.id);
			});

			if (this.API.useAudioTracksInHlsPlugin) {
				this.player.on(this.HlsEvents.MANIFEST_LOADED, (event, data) => {
					if (data.audioTracks?.length && data.audioTracks?.length > 1) {
						this.API.setAudioTracks(data.audioTracks);
						this.API.setAudioTrackInPlayer();
					}
				});
			}
		}
	}
}
